import React, { useRef } from "react";

/**
 * FilterSection - Displays and manages filter options for images
 * 
 * Features:
 * - Shows available filter buttons (Normal, Grayscale, Sepia, etc.)
 * - Applies selected filter to active image using canvas
 * - Automatically scrolls to active filter when switching images
 * - Dynamically positioned above bottom panel
 * - Maintains filter state per image
 */
const FilterSection = ({
  filterMap,           // Object mapping filter names to CSS filter values
  canvasRef,           // Canvas reference for applying filters
  activeImage,         // Currently selected image object (null when camera is open)
  setSelectedImages,   // Function to update images in parent state
  selectedImages,      // Array of all selected images with their filters
  activeFilter,        // Currently active filter name
  setActiveFilter,     // Function to update active filter state
  filterPanelBottom,   // Dynamic bottom positioning based on bottom panel height
  activeImageId        // ID of currently active image for filter lookup
}) => {
  // Reference to filters panel container for scrolling functionality
  const filtersPanelRef = useRef(null);

  /**
   * Applies filters to image data for iOS compatibility
   * 
   * iOS Safari has limited support for canvas CSS filters, so we implement
   * pixel-level manipulation for common filter effects.
   * 
   * @param {Uint8ClampedArray} data - Image pixel data array
   * @param {string} filterType - Type of filter to apply
   * @returns {void} - Modifies data array in place
   */
  const applyIOSFilter = (data, filterType) => {
    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];     // Red
      const g = data[i + 1]; // Green
      const b = data[i + 2]; // Blue
      const a = data[i + 3]; // Alpha
      
      let newR = r, newG = g, newB = b;
      
      switch (filterType) {
        case 'BW':
          // Convert to grayscale
          const gray = r * 0.299 + g * 0.587 + b * 0.114;
          newR = newG = newB = gray * 1.1; // Enhanced contrast
          break;
          
        case 'Sepia':
          // Sepia tone conversion
          newR = Math.min(255, (r * 0.393) + (g * 0.769) + (b * 0.189));
          newG = Math.min(255, (r * 0.349) + (g * 0.686) + (b * 0.168));
          newB = Math.min(255, (r * 0.272) + (g * 0.534) + (b * 0.131));
          break;
          
        case 'Warm':
          // Warm tones
          newR = Math.min(255, r * 1.05 * 1.3 * 0.8 + g * 1.3 * 0.2);
          newG = Math.min(255, g * 1.05 * 1.3 * 0.8 + r * 1.3 * 0.2);
          newB = Math.min(255, b * 1.05 * 1.3 * 0.8);
          break;
          
        case 'Cool':
          // Cool tones
          newR = Math.min(255, r * 1.1 * 0.85 * 0.99);
          newG = Math.min(255, g * 1.1 * 0.85 * 0.99);
          newB = Math.min(255, b * 1.1 * 0.85 * 0.99);
          break;
          
        case 'Vintage':
          // Vintage effect
          newR = Math.min(255, (r * 0.393) + (g * 0.769) + (b * 0.189) * 1.1 * 1.05);
          newG = Math.min(255, (r * 0.349) + (g * 0.686) + (b * 0.168) * 1.1 * 1.05);
          newB = Math.min(255, (r * 0.272) + (g * 0.534) + (b * 0.131) * 1.1 * 1.05);
          break;
          
        case 'Vivid':
          // Highly saturated
          newR = Math.min(255, r * 1.6 * 1.15);
          newG = Math.min(255, g * 1.6 * 1.15);
          newB = Math.min(255, b * 1.6 * 1.15);
          break;
          
        case 'Invert':
          // Color inversion
          newR = 255 - r;
          newG = 255 - g;
          newB = 255 - b;
          break;
          
        default:
          // For other filters, apply basic adjustments
          if (filterType !== 'Normal') {
            // Simple brightness/contrast adjustment
            newR = Math.min(255, r * 1.05);
            newG = Math.min(255, g * 1.05);
            newB = Math.min(255, b * 1.05);
          }
          break;
      }
      
      data[i] = newR;
      data[i + 1] = newG;
      data[i + 2] = newB;
      // Alpha channel remains unchanged
    }
  };

  /**
   * applyFilter - Applies a filter to the active image
   * 
   * @param {string} filter - The filter name to apply (e.g., "Normal", "Grayscale")
   * 
   * Process:
   * 1. Updates global active filter state (affects camera preview)
   * 2. If no image selected, camera preview handles the filter
   * 3. For "Normal" filter, restores original image
   * 4. For other filters, applies CSS filter using canvas
   * 5. Updates image data with filtered version and filter name
   */
  const applyFilter = React.useCallback((filter) => {
    // 🔥 Always update active filter (affects camera preview when no image selected)
    setActiveFilter(filter);

    // 🧠 If no image selected → camera preview handles it
    if (!activeImage) return;

    // Restore original image for "Normal" filter
    if (filter === "Normal") {
      setSelectedImages((prev) =>
        prev.map((img) =>
          img.id === activeImage.id
            ? { ...img, filtered: img.original, filter }
            : img
        )
      );
      return;
    }

    // Apply CSS filter to selected image using canvas
    const imgEl = new Image();
    imgEl.src = activeImage.original;

    imgEl.onload = () => {
      const canvas = canvasRef.current;
      canvas.width = imgEl.width;
      canvas.height = imgEl.height;

      const ctx = canvas.getContext("2d");
      
      // Check if iOS and apply filter workaround
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      if (isIOS && filter !== "Normal") {
        // iOS Safari doesn't support canvas filters well - apply pixel manipulation
        ctx.drawImage(imgEl, 0, 0);
        
        // Get image data for pixel manipulation
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imageData.data;
        
        // Apply filter based on filter type
        applyIOSFilter(data, filter);
        
        // Put modified image data back
        ctx.putImageData(imageData, 0, 0);
      } else {
        // Apply CSS filter to canvas context (works on most browsers)
        ctx.filter = filterMap[filter];  // Apply CSS filter (e.g., "grayscale(100%)")
        ctx.drawImage(imgEl, 0, 0);
      }

      const filtered = canvas.toDataURL("image/png");

      // Update image with filtered version
      setSelectedImages((prev) =>
        prev.map((img) =>
          img.id === activeImage.id
            ? { ...img, filtered, filter }
            : img
        )
      );
    };
  }, [activeImage, filterMap, setActiveFilter, setSelectedImages, canvasRef]);

  /**
   * filterApplied - Gets the current filter applied to the active image
   * 
   * Logic:
   * - Find image by activeImageId in selectedImages array
   * - Return its filter property, or 'Normal' if not found
   */
  const filterApplied = selectedImages.find(img => img.id === activeImageId)?.filter || activeFilter || 'Normal';

  /**
   * Scroll to active filter button when activeImageId changes
   * 
   * Process:
   * 1. Find all filter buttons in the panel
   * 2. Get index of the currently applied filter
   * 3. Smooth scroll to center the active button
   * 
   * Ensures user can always see the active filter when switching images
   */
  React.useEffect(() => {
    if (filtersPanelRef.current && filterApplied) {
      const filterButtons = filtersPanelRef.current.querySelectorAll('.filter-btn');
      const activeButtonIndex = Object.keys(filterMap).indexOf(filterApplied);
      
      if (activeButtonIndex >= 0 && filterButtons[activeButtonIndex]) {
        filterButtons[activeButtonIndex].scrollIntoView({
          behavior: 'smooth',    // Smooth scrolling animation
          inline: 'center',      // Center horizontally in scrollable container
          block: 'nearest'       // Minimal vertical movement
        });
      }
    }
  }, [activeImageId, filterApplied, filterMap]);

  return (
    <div 
      className="filters-panel" 
      style={{ bottom: `${filterPanelBottom}px` }}
      ref={filtersPanelRef}
    >
      {Object.keys(filterMap).map((filter) => (
        <button
          key={filter}
          className={`filter-btn ${
            filterApplied === filter ? "active" : ""
          }`}
          onClick={() => applyFilter(filter)}
          title={`Apply ${filter} filter`}
        >
          {filter}
        </button>
      ))}
    </div>
  );
};

export default FilterSection;
